import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import { Voucher } from '../../../api/types/voucher';
import PageError from '../../../common/PageError';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import { Loader } from '../../../components/Loader';
import RecordsPagination from '../../../components/hub/RecordsPagination';
import { NO_OF_VOUCHERS_PER_PAGE } from '../../../util/constants';
import { BookOneOffSession } from './BookOneOffSession';
import VoucherFilter from './VoucherFilter';
import { VoucherListItem } from './VoucherListItem';
import useVoucherContext, { withVoucherContextProvider } from './useVoucherContext';

const Vouchers = () => {
  const { vouchers, filteredVouchers, fetchVoucherIsError, fetchVoucherIsLoading } = useVoucherContext();
  const [vouchersInView, setVouchersInView] = useState<Voucher[]>([]);
  const [startIndex, setStartIndex] = useState(0);

  const getVouchersInViewByIndex = (sortedVouchers: Voucher[], startIndex: number) => {
    const totalVouchers = sortedVouchers.length;
    if (startIndex >= totalVouchers) {
      return [];
    }

    return sortedVouchers.slice(startIndex, startIndex + NO_OF_VOUCHERS_PER_PAGE);
  };

  useEffect(() => {
    if (filteredVouchers.length > 0) {
      const vouchersInView = getVouchersInViewByIndex(filteredVouchers, 0);
      setVouchersInView((current) => {
        if (current.length === vouchersInView.length && JSON.stringify(current) === JSON.stringify(vouchersInView)) {
          return current;
        }
        return vouchersInView;
      });
    } else {
      setVouchersInView([]);
    }
  }, [filteredVouchers]);

  const handlePaginationClick = (newStartIndex: number) => {
    setVouchersInView(getVouchersInViewByIndex(filteredVouchers, newStartIndex));
    setStartIndex(newStartIndex);
  };

  return (
    <HubPageLayout>
      {fetchVoucherIsLoading ? (
        <Loader />
      ) : fetchVoucherIsError ? (
        <PageError activePage="hub-page" retryEnabled={false} />
      ) : (
        <>
          <div className="flex flex-col gap-y-4 rounded-3xl border border-grey-100 bg-white p-5 shadow-md md:p-10">
            <p className="text-lg">Use Cluey Vouchers to book sessions across any active enrolment in your account</p>
            {!isEmpty(vouchers) ? (
              <>
                <div className="flex flex-col justify-between lg:flex-row">
                  <VoucherFilter
                    filterClassName="pt-3 pb-4 lg:pb-4"
                    labelClassName="font-bold"
                    hideFiltersOnDefault={false}
                    filtersOccupyWidth={false}
                  />
                  <div className="flex flex-col gap-2">
                    <BookOneOffSession
                      buttonText="Book a session"
                      buttonClassName="poppins mt-3 h-auto rounded-full !font-medium !border-blood-orange-4 !bg-blood-orange-4 !px-4 py-[6px] font-medium"
                    />
                  </div>
                </div>
                {!isEmpty(vouchersInView) ? (
                  <>
                    <div className="flex flex-col gap-4 pt-2 lg:grid lg:grid-cols-2 lg:pt-0">
                      {vouchersInView.map((voucher) => (
                        <VoucherListItem key={voucher.id} voucher={voucher} />
                      ))}
                    </div>
                    <RecordsPagination
                      recordsTotal={filteredVouchers.length}
                      noOfRecordsPerPage={NO_OF_VOUCHERS_PER_PAGE}
                      handlePaginationClick={handlePaginationClick}
                      pageIndex={startIndex}
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-grey-3 p-7">
                    <p className="text-lg font-bold">No vouchers found</p>
                    <p>Try adjusting your filter to find what you&apos;re looking for</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-grey-3 p-7">
                <p className="text-lg font-bold">No vouchers available</p>
                <p>Cluey Vouchers may take a few minutes to generate, and will appear here when available.</p>
              </div>
            )}
          </div>
        </>
      )}
    </HubPageLayout>
  );
};

export default withVoucherContextProvider(Vouchers);
